import type { ProviderConfig } from "@/api/providers";

export type ProviderTypeFilter = "all" | "ats" | "board" | "local-parser" | "signal";
export type ProviderStatusFilter = "all" | "enabled" | "disabled";

export const TYPE_LABELS: Record<string, string> = {
  ats: "ATS careers",
  board: "Hiring board",
  "local-parser": "Custom parser",
  signal: "Market signal",
};

export const TYPE_COLORS: Record<string, string> = {
  ats: "bg-sky-500/10 text-sky-400",
  board: "bg-emerald-500/10 text-emerald-400",
  "local-parser": "bg-violet-500/10 text-violet-400",
  signal: "bg-amber-500/10 text-amber-400",
};

export const FEATURE_LABELS: Record<string, string> = {
  detect: "Auto-detect",
  "api-url": "API URL override",
  pagination: "Pagination",
  salary: "Comp data",
  description: "Full descriptions",
  "posted-at": "Posted date",
  "multi-location": "Multi-location",
};

export const TYPE_FILTER_TABS: ProviderTypeFilter[] = [
  "all",
  "ats",
  "board",
  "local-parser",
  "signal",
];

export const STATUS_FILTER_TABS: ProviderStatusFilter[] = [
  "all",
  "enabled",
  "disabled",
];

export function featureLabel(feature: string): string {
  return FEATURE_LABELS[feature] ?? feature;
}

export function countEnabledProviders(providers: ProviderConfig[]): number {
  return providers.filter((p) => p.enabled).length;
}

export interface ProviderFilterOptions {
  search?: string;
  type?: ProviderTypeFilter;
  status?: ProviderStatusFilter;
}

export function filterProviders(
  providers: ProviderConfig[],
  { search = "", type = "all", status = "all" }: ProviderFilterOptions,
): ProviderConfig[] {
  const q = search.trim().toLowerCase();

  return providers.filter((p) => {
    if (type !== "all" && p.type !== type) return false;
    if (status === "enabled" && !p.enabled) return false;
    if (status === "disabled" && p.enabled) return false;
    if (q) {
      return (
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.providerId.toLowerCase().includes(q)
      );
    }
    return true;
  });
}

export function typeHelpText(type: ProviderConfig["type"]): string {
  switch (type) {
    case "board":
      return "Hiring board aggregators feed signal scan when you select them under Intake → Providers.";
    case "ats":
      return "ATS careers scanners activate when you track a company that uses this provider on Intake → Sources.";
    case "local-parser":
      return "Custom parsers extract hiring signals from careers pages via local scripts.";
    case "signal":
      return "Market signal providers power pulse (news, funding, tech, regulatory, intent). Enable here and select them under Intake → Providers; connect credentials in Integrations when required.";
  }
}

/** Providers the user can opt into via Intake signalSources */
export function isSelectableSignalSource(provider: ProviderConfig): boolean {
  if (!provider.enabled) return false;
  if (provider.type === "board" || provider.type === "signal") {
    return provider.implemented !== false;
  }
  return false;
}

export function hasIntegrationDetails(provider: ProviderConfig): boolean {
  return !!(
    provider.urlPattern ||
    (provider.allowedHosts && provider.allowedHosts.length > 0) ||
    provider.supportsDetect !== undefined ||
    provider.supportsApiUrl !== undefined ||
    provider.timeoutMs !== undefined ||
    provider.retryPolicy
  );
}

export function hasConfigData(config: Record<string, unknown> | undefined): boolean {
  return !!config && Object.keys(config).length > 0;
}

export function formatProviderDate(iso?: string): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}
